import { useState } from "react";
import { apiFetch } from "../lib/api";

const competitors = [
  {
    name: "ERP Genérico",
    price: "R$ 500/mês",
    features: {
      modules: 3,
      users: 10,
      support: "E-mail",
      training: "Online",
      api: "Básica",
      sla: "Não",
      customization: "Não",
    },
  },
  {
    name: "ZWeb Professional",
    price: "R$ 799/mês",
    features: {
      modules: 10,
      users: 50,
      support: "Prioritário",
      training: "Presencial",
      api: "Completa",
      sla: "Sim",
      customization: "Parcial",
    },
  },
  {
    name: "ZWeb Enterprise",
    price: "R$ 1.999/mês",
    features: {
      modules: 15,
      users: "Ilimitado",
      support: "24/7 Dedicado",
      training: "Dedicado",
      api: "Ilimitada",
      sla: "Garantido",
      customization: "Completa",
    },
  },
];

const criteria = [
  { key: "modules", label: "Módulos", weight: 1.5 },
  { key: "users", label: "Usuários", weight: 1 },
  { key: "support", label: "Suporte", weight: 1.2 },
  { key: "training", label: "Treinamento", weight: 1 },
  { key: "api", label: "API", weight: 1.1 },
  { key: "sla", label: "SLA", weight: 1.2 },
  { key: "customization", label: "Customização", weight: 1.3 },
];

export default function ComparisonPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [currentERP, setCurrentERP] = useState("");
  const [companySize, setCompanySize] = useState("medium");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiFetch("/api/comparison", {
        method: "POST",
        body: JSON.stringify({
          name,
          email,
          phone,
          company,
          currentERP,
          companySize,
        }),
      });
      setSubmitted(true);
    } catch {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/solicitar-demo" className="text-sm font-medium hover:text-accent-600">Demo</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold">Comparativo</h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Compare o ZWeb com outros ERPs do mercado.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="mt-12 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="p-4 text-left font-semibold">Funcionalidade</th>
                  {competitors.map((comp) => (
                    <th key={comp.name} className="p-4 text-center font-semibold">
                      {comp.name}
                      <div className="mt-1 text-sm font-normal text-gray-600 dark:text-gray-400">
                        {comp.price}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {criteria.map((criterion) => (
                  <tr key={criterion.key} className="border-b border-gray-100 dark:border-gray-800/50">
                    <td className="p-4 font-medium">{criterion.label}</td>
                    {competitors.map((comp) => {
                      const value = comp.features[criterion.key as keyof typeof comp.features];
                      const isBest =
                        criterion.key === "users"
                          ? value === "Ilimitado"
                          : typeof value === "number"
                          ? value === Math.max(...competitors.map((c) => c.features[criterion.key as keyof typeof c.features] as number))
                          : value === "24/7 Dedicado" || value === "Garantido" || value === "Completa" || value === "Completa" || value === "Ilimitada";

                      return (
                        <td
                          key={criterion.key + comp.name}
                          className={`p-4 text-center ${isBest ? "font-semibold text-green-600" : ""}`}
                        >
                          {typeof value === "number" ? value : value}
                          {isBest && criterion.key === "modules" && "+"}
                          {typeof value === "number" && criterion.key === "users" && "+"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Contact Form */}
          <div className="mt-16 rounded-2xl border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="text-2xl font-bold text-center">Solicite uma Comparação Personalizada</h2>
            <p className="mt-2 text-center text-gray-600 dark:text-gray-400">
              Preencha o formulário e receberá um comparativo detalhado.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 mx-auto max-w-md space-y-4">
              {submitted ? (
                <div className="rounded-lg bg-green-50 p-4 text-center text-green-700 dark:bg-green-900/50 dark:text-green-400">
                  Obrigado! Enviaremos o comparativo para seu e-mail.
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Nome</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">E-mail</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Telefone</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Empresa</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">ERP Atual</label>
                    <input
                      type="text"
                      value={currentERP}
                      onChange={(e) => setCurrentERP(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                      placeholder="Ex: TOTVS, Linx, etc."
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">Porte da Empresa</label>
                    <select
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    >
                      <option value="micro">Micro (até 9 funcionários)</option>
                      <option value="small">Pequena (10-49 funcionários)</option>
                      <option value="medium">Média (50-249 funcionários)</option>
                      <option value="large">Grande (250+ funcionários)</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-accent-600 px-4 py-3 font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                  >
                    {loading ? "Enviando..." : "Enviar Comparativo"}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}